import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import Footer from "@/components/layout/Footer";
import { ArrowLeft, Calendar, User } from "lucide-react";
import BlogShareButton from "@/components/BlogShareButton";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-main py-20">
          <div className="max-w-3xl mx-auto">
            <div className="skeleton h-8 w-1/3 mb-4" />
            <div className="skeleton h-12 w-full mb-6" />
            <div className="skeleton aspect-video mb-8" />
            <div className="space-y-4">
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-main py-20 text-center">
          <h1 className="text-3xl font-display font-bold mb-4">Post not found</h1>
          <Link to="/blog" className="text-[#5170FF] hover:text-[#FF2D87]">
            ← Back to Resources
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <Navbar />
      <DashboardHeader />

      <article className="py-12 md:py-20">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            {/* Back Link */}
            <div className="flex items-center justify-between mb-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#0A0A0A]"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Resources
              </Link>
              <BlogShareButton
                title={post.title}
                url={typeof window !== "undefined" ? window.location.href : ""}
                excerpt={post.excerpt}
                slug={post.slug}
              />
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Meta */}
            {(!post.hide_author || !post.hide_date) && (
              <div className="flex flex-wrap gap-6 text-sm text-[#4A4A4A] mb-8 pb-8 border-b-2 border-[#E5E5E5]">
                {!post.hide_author && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {post.author_name}
                  </div>
                )}
                {!post.hide_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {formatDate(post.created_at)}
                  </div>
                )}
              </div>
            )}

            {/* Featured Image */}
            {post.image_url && (
              <div className="card-brutal overflow-hidden mb-12">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full aspect-video object-cover"
                />
              </div>
            )}

            {/* Excerpt */}
            <p className="text-xl text-[#4A4A4A] mb-8 font-display italic">
              {post.excerpt}
            </p>

            {/* Content */}
            {post.content && /<[a-z][\s\S]*>/i.test(post.content) ? (
              <div
                className="blog-content prose prose-lg max-w-none"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
                dangerouslySetInnerHTML={{ __html: post.content }}
                data-testid="blog-post-content"
              />
            ) : (
              <div
                className="prose prose-lg max-w-none"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
                data-testid="blog-post-content"
              >
                {(post.content || "").split("\n\n").map((paragraph, index) => (
                  <p key={index} className="mb-6 text-[#0A0A0A] leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            )}

            {/* Tags — sharp rectangles, baby pink → hot pink */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t-2 border-[#E5E5E5]">
                <p className="text-sm font-bold uppercase tracking-wider text-[#4A4A4A] mb-4">
                  Filed under
                </p>
                <div className="flex flex-wrap gap-2" data-testid="blog-post-tags">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-4 py-2 text-xs font-bold uppercase tracking-wider border-2 border-[#0A0A0A] bg-[#FFC2E8] text-[#0A0A0A] hover:bg-[#FF2D87] hover:text-white transition-colors cursor-default"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-12 pt-8 border-t-2 border-[#E5E5E5] flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm font-bold uppercase tracking-wider text-[#4A4A4A]">
                Share this article
              </p>
              <BlogShareButton
                title={post.title}
                url={typeof window !== "undefined" ? window.location.href : ""}
                excerpt={post.excerpt}
                slug={post.slug}
              />
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
